import type * as React from 'react';
import { useEffect, useRef } from 'react';
import cx from 'classnames';
import { Icon } from '@dx-ui/osc-icon';
import type { IconProps } from '@dx-ui/osc-icon';
import { announce } from '@dx-ui/utilities-accessibility';
import { useForkedRef } from '@dx-ui/utilities-use-forked-ref';

export type BannerNotificationProps = {
  /** Content to be displayed in banner notification. */
  children: React.ReactNode;
  /** The status to be used. */
  status: 'error' | 'success' | 'normal' | 'warning' | 'info' | 'honors';
  customIcon?: IconProps | null;
  /** When true, reduces vertical padding and fixes horizontal padding at px-4 for dense layouts. */
  isCompact?: boolean;
  'data-testid'?: string;
} & React.ComponentProps<'div'>;

/**
 * Displays a banner notification message with an icon.
 *
 * **Accessibility:** Announcements are made via a singleton pair of off-screen live regions
 * managed by `@dx-ui/utilities-accessibility`. The live regions are created lazily on first
 * use and shared across all components on the page. This means:
 *
 * - Identical messages re-announce correctly (each call is a distinct DOM mutation).
 * - Assertive (error) and polite (all other statuses) politeness levels are handled separately.
 * - The first announcement is delayed by 100 ms so the AT has time to register the live
 *   regions before any content is inserted.
 *
 * **Do not conditionally mount/unmount this component.** Instead, conditionally render its
 * `children`. When `children` is `null` the banner is visually hidden via `sr-only` and no
 * announcement fires. When `children` are provided the banner becomes visible and the AT
 * announces the rendered text.
 *
 * If you wrap this component in a container, keep that container in the DOM at all times
 * (use `sr-only` to hide it visually when idle).
 */
export const BannerNotification = ({
  status,
  children,
  className,
  customIcon,
  isCompact = false,
  ref,
  'data-testid': dataTestId,
  ...rest
}: BannerNotificationProps) => {
  const statusIconMap: Partial<Record<BannerNotificationProps['status'], React.ReactElement>> = {
    success: <Icon name="check-circle" variant="solid" />,
    error: <Icon name="close-circle" variant="solid" />,
    warning: <Icon name="alert-triangle" variant="solid" />,
    info: <Icon name="info-circle" variant="regular" />,
  };

  let bannerIcon: React.ReactElement | null = null;
  if (customIcon !== null) {
    bannerIcon = customIcon ? <Icon {...customIcon} /> : (statusIconMap[status] ?? null);
  }

  const sharedClassName = cx(
    'w-full @container',
    isCompact ? 'py-2' : 'py-4',
    {
      'bg-surface-success text-surface-success': status === 'success',
      'bg-surface-alert-default text-surface-alert-default': status === 'normal',
      'bg-surface-error text-surface-alert-error': status === 'error',
      'bg-surface-warn text-surface-warn': status === 'warning',
      'bg-surface-info text-surface-info': status === 'info',
      'bg-surface-honors text-surface-honors': status === 'honors',
    },
    className
  );

  const innerLayout = cx(
    'mx-auto flex w-full max-w-screen-2xl items-center gap-x-2',
    isCompact ? 'px-3' : 'px-4 @md:px-8 @lg:px-10'
  );

  const showErrorBanner = status === 'error' && children;
  const showStatusBanner = status !== 'error' && children;

  const errorRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const forkedErrorRef = useForkedRef(errorRef, status === 'error' ? ref : null);
  const forkedStatusRef = useForkedRef(statusRef, status !== 'error' ? ref : null);

  useEffect(() => {
    if (showErrorBanner && errorRef.current) {
      const text = errorRef.current.textContent?.trim();
      if (text) announce(text, 'assertive');
    }
  }, [showErrorBanner]);

  useEffect(() => {
    if (showStatusBanner && statusRef.current) {
      const text = statusRef.current.textContent?.trim();
      if (text) announce(text, 'polite');
    }
  }, [showStatusBanner]);

  return (
    <>
      {/* Error banner */}
      <div
        ref={forkedErrorRef}
        role={showErrorBanner ? 'alert' : undefined}
        className={cx({ 'sr-only': !showErrorBanner, [sharedClassName]: showErrorBanner })}
        {...rest}
        data-testid={status === 'error' && dataTestId ? dataTestId : 'errorBannerNotification'}
      >
        <div className={innerLayout}>
          {bannerIcon && <span className="shrink-0 self-start">{bannerIcon}</span>}
          {showErrorBanner ? children : null}
        </div>
      </div>
      {/* Status banner */}
      <div
        ref={forkedStatusRef}
        role={showStatusBanner ? 'status' : undefined}
        className={cx({ 'sr-only': !showStatusBanner, [sharedClassName]: showStatusBanner })}
        {...rest}
        data-testid={
          status !== 'error' && dataTestId ? dataTestId : 'successOrNormalBannerNotification'
        }
      >
        <div className={innerLayout}>
          {bannerIcon && <span className="shrink-0 self-start">{bannerIcon}</span>}
          {showStatusBanner ? children : null}
        </div>
      </div>
    </>
  );
};
